import React, { useState, useEffect, useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlass, Lightning, Target, ChartBar, Lightbulb, Strategy,
  ArrowsClockwise, FloppyDisk, Trash, X, CaretRight, CaretDown,
  Globe, InstagramLogo, Buildings, Star, TrendUp, Eye, Scales,
  Clock, ArrowRight, CheckCircle, Warning
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { useUpgrade } from '../components/UpgradeSystem';
import { EarlyAccessBanner } from '../components/EarlyAccessBanner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ Zustand Store ═══ */
const useCompIntelStore = create(
  persist(
    (set) => ({
      query: '', targetMarket: '', productCategory: '', targetAudience: '',
      report: null, reportId: null, sourceMeta: null, loading: false, loadingStep: 0, error: null,
      savedReports: [], drawerOpen: false, compareModal: null, compareResult: null, compareLoading: false,
      setField: (k, v) => set({ [k]: v }),
      setReport: (report, reportId, sourceMeta) => set({ report, reportId, sourceMeta, loading: false, error: null }),
      setLoading: (loading, step) => set({ loading, loadingStep: step || 0 }),
      setError: (error) => set({ error, loading: false }),
      setSavedReports: (r) => set({ savedReports: r }),
      reset: () => set({ report: null, reportId: null, sourceMeta: null, error: null, loadingStep: 0 }),
    }),
    {
      name: 'scalitos-comp-intel',
      partialize: (s) => ({
        query: s.query, targetMarket: s.targetMarket, productCategory: s.productCategory,
        targetAudience: s.targetAudience, report: s.report, reportId: s.reportId, sourceMeta: s.sourceMeta, savedReports: s.savedReports,
      }),
    }
  )
);

/* ═══ Source Transparency Badge ═══ */
const SourceBadge = ({ sourceMeta }) => {
  if (!sourceMeta) return null;
  const { source_type, source_label, source_description } = sourceMeta;

  const config = {
    live_scraped: { bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.2)', color: '#22d3ee', icon: CheckCircle },
    partial_content: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', color: '#f59e0b', icon: Warning },
    blocked: { bg: 'rgba(255,68,68,0.06)', border: 'rgba(255,68,68,0.15)', color: '#ff6b6b', icon: Warning },
    ai_knowledge: { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.2)', color: '#a78bfa', icon: Lightning },
  };

  const c = config[source_type] || config.ai_knowledge;
  const Icon = c.icon;

  return (
    <div data-testid="source-transparency-badge" className="flex items-start gap-2.5 px-4 py-3 rounded-xl" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <Icon size={16} weight="fill" className="mt-0.5 shrink-0" style={{ color: c.color }} />
      <div>
        <p className="text-xs font-semibold" style={{ color: c.color }}>{source_label || 'AI knowledge-based'}</p>
        <p className="text-[11px] mt-0.5" style={{ color: 'rgba(245,240,232,0.45)' }}>{source_description}</p>
      </div>
    </div>
  );
};

/* ═══ Loading Steps ═══ */
const LOADING_STEPS = [
  'Checking if URL is scrapable...',
  'Fetching live website data...',
  'Analyzing brand identity...',
  'Detecting niche and market signals...',
  'Identifying direct competitors...',
  'Finding aspirational and emerging players...',
  'Analyzing positioning and content patterns...',
  'Discovering opportunity gaps...',
  'Generating strategic recommendations...',
];

/* ═══ Section Components ═══ */
const Sec = ({ title, icon: Icon, children, color = '#c8f060' }) => (
  <div className="rounded-xl p-5 lg:p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon size={16} style={{ color }} weight="fill" />
      </div>
      <h3 className="text-base lg:text-lg font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const safe = (v) => (v == null ? '' : typeof v === 'object' ? (v.line || v.text || v.name || v.label || JSON.stringify(v)) : String(v));

const Tag = ({ children, color = 'rgba(200,240,96,0.1)', textColor = '#c8f060' }) => (
  <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ background: color, color: textColor }}>{safe(children)}</span>
);

const InsightItem = ({ text }) => (
  <div className="flex gap-2.5 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
    <ArrowRight size={12} className="mt-1 shrink-0" style={{ color: '#c8f060' }} />
    <span className="text-sm" style={{ color: 'rgba(245,240,232,0.7)' }}>{safe(text)}</span>
  </div>
);

/* ═══ Brand Overview ═══ */
const BrandOverview = ({ data }) => {
  if (!data) return null;
  const fields = [
    { label: 'Niche', value: data.niche },
    { label: 'Audience', value: data.audience },
    { label: 'Price Tier', value: data.priceTier },
    { label: 'Tone', value: data.tone },
    { label: 'Category', value: data.category },
    { label: 'Position', value: data.marketPosition },
  ];
  return (
    <Sec title="Your Brand Overview" icon={Eye} color="#22d3ee">
      {data.summary && <p className="text-sm mb-4" style={{ color: 'rgba(245,240,232,0.6)' }}>{data.summary}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(f => f.value && (
          <div key={f.label} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{f.label}</p>
            <p className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{f.value}</p>
          </div>
        ))}
      </div>
    </Sec>
  );
};

/* ═══ Competitor Card ═══ */
const CompetitorCard = ({ comp, onCompare }) => (
  <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
    <div className="flex items-start justify-between mb-2">
      <div>
        <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{comp.name}</p>
        <div className="flex gap-2 mt-1">
          {comp.website && comp.website !== 'N/A' && <a href={comp.website} target="_blank" rel="noreferrer" className="text-[10px] flex items-center gap-1 hover:underline" style={{ color: 'rgba(245,240,232,0.35)' }}><Globe size={10} /> Website</a>}
          {comp.instagram && comp.instagram !== 'N/A' && <span className="text-[10px] flex items-center gap-1" style={{ color: 'rgba(245,240,232,0.35)' }}><InstagramLogo size={10} /> {comp.instagram}</span>}
        </div>
      </div>
      {comp.priceLevel && <Tag>{comp.priceLevel}</Tag>}
    </div>
    <p className="text-xs my-2" style={{ color: 'rgba(245,240,232,0.5)' }}>{comp.description}</p>
    {comp.relevanceReason && <p className="text-[11px] italic mb-3" style={{ color: 'rgba(200,240,96,0.5)' }}>{comp.relevanceReason}</p>}
    <div className="grid grid-cols-2 gap-3 mb-3">
      <div>
        <p className="text-[9px] uppercase tracking-wider mb-1.5" style={{ color: 'rgba(200,240,96,0.4)' }}>Strengths</p>
        {(comp.strengths || []).map((s, i) => <p key={i} className="text-[11px] mb-0.5" style={{ color: 'rgba(245,240,232,0.5)' }}>+ {s}</p>)}
      </div>
      <div>
        <p className="text-[9px] uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,100,100,0.4)' }}>Weaknesses</p>
        {(comp.weaknesses || []).map((w, i) => <p key={i} className="text-[11px] mb-0.5" style={{ color: 'rgba(245,240,232,0.4)' }}>- {w}</p>)}
      </div>
    </div>
    <button onClick={() => onCompare(comp)} data-testid={`compare-${comp.name}`}
      className="w-full py-2 rounded-lg text-[11px] font-medium flex items-center justify-center gap-1.5 transition-all hover:bg-white/5"
      style={{ border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.5)' }}>
      <Scales size={12} /> Compare
    </button>
  </div>
);

/* ═══ Competitor Tabs ═══ */
const CompetitorTabs = ({ competitors, onCompare }) => {
  const [tab, setTab] = useState('direct');
  const tabs = [
    { id: 'direct', label: 'Direct', data: competitors?.direct || [] },
    { id: 'aspirational', label: 'Aspirational', data: competitors?.aspirational || [] },
    { id: 'emerging', label: 'Emerging', data: competitors?.emerging || [] },
  ];
  const activeData = tabs.find(t => t.id === tab)?.data || [];

  return (
    <Sec title="Top Competitors" icon={Target} color="#f59e0b">
      <div className="flex gap-1 p-1 rounded-lg mb-4" style={{ background: 'rgba(255,255,255,0.02)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`comp-tab-${t.id}`}
            className="flex-1 py-2 rounded-md text-xs font-medium transition-all text-center"
            style={{
              background: tab === t.id ? 'rgba(200,240,96,0.08)' : 'transparent',
              color: tab === t.id ? '#c8f060' : 'rgba(245,240,232,0.3)',
            }}>
            {t.label} ({t.data.length})
          </button>
        ))}
      </div>
      {activeData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {activeData.map((c, i) => <CompetitorCard key={i} comp={c} onCompare={onCompare} />)}
        </div>
      ) : (
        <p className="text-xs text-center py-6" style={{ color: 'rgba(245,240,232,0.2)' }}>No competitors in this category</p>
      )}
    </Sec>
  );
};

/* ═══ Compare Modal ═══ */
const CompareModal = ({ competitor, result, loading, onClose, onRun, reportId }) => (
  <AnimatePresence>
    {competitor && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
          className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
              Compare with {competitor.name}
            </h3>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5"><X size={18} style={{ color: 'rgba(245,240,232,0.4)' }} /></button>
          </div>
          {!result && !loading && (
            <div className="text-center py-8">
              <p className="text-sm mb-4" style={{ color: 'rgba(245,240,232,0.5)' }}>Run a detailed comparison to see positioning, strengths, and how to outperform.</p>
              <button onClick={() => onRun(reportId, competitor)} data-testid="run-compare"
                className="px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#c8f060', color: '#080808' }}>
                Run Comparison (3 credits)
              </button>
            </div>
          )}
          {loading && (
            <div className="text-center py-12">
              <ArrowsClockwise size={24} className="animate-spin mx-auto mb-3" style={{ color: '#c8f060' }} />
              <p className="text-sm" style={{ color: 'rgba(245,240,232,0.4)' }}>Analyzing competitive positioning...</p>
            </div>
          )}
          {result?.comparison && (
            <div className="space-y-4">
              {['positioning', 'pricing', 'contentStyle', 'messaging'].map(key => {
                const d = result.comparison[key];
                if (!d) return null;
                return (
                  <div key={key} className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                    <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'rgba(200,240,96,0.4)' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div><p className="text-[9px] mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>You</p><p className="text-xs" style={{ color: '#f5f0e8' }}>{d.user}</p></div>
                      <div><p className="text-[9px] mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>{competitor.name}</p><p className="text-xs" style={{ color: '#f5f0e8' }}>{d.competitor}</p></div>
                    </div>
                    <p className="text-[11px] italic" style={{ color: 'rgba(200,240,96,0.5)' }}>{d.verdict}</p>
                  </div>
                );
              })}
              {result.comparison.recommendedEdge && (
                <div className="p-4 rounded-lg" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.1)' }}>
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#c8f060' }}>Your Competitive Edge</p>
                  <p className="text-sm" style={{ color: '#f5f0e8' }}>{result.comparison.recommendedEdge}</p>
                </div>
              )}
              {result.comparison.howToOutperform && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>How to outperform</p>
                  {result.comparison.howToOutperform.map((a, i) => <InsightItem key={i} text={a} />)}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ═══ Main Page ═══ */
export default function CompetitiveIntelligencePage() {
  const store = useCompIntelStore();
  const { handleApiError } = useUpgrade();
  const [optionsOpen, setOptionsOpen] = useState(false);

  // Loading step animation
  useEffect(() => {
    if (!store.loading) return;
    const interval = setInterval(() => {
      store.setLoading(true, Math.min((store.loadingStep || 0) + 1, LOADING_STEPS.length - 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [store.loading, store.loadingStep]);

  // Load saved reports on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/competitive-intel/history`);
      store.setSavedReports(data.reports || []);
    } catch {}
  };

  const analyze = useCallback(async () => {
    if (!store.query.trim()) return;
    store.setLoading(true, 0);
    store.setField('error', null);
    store.setField('report', null);
    try {
      const { data } = await axios.post(`${API}/api/competitive-intel/analyze`, {
        query: store.query,
        target_market: store.targetMarket,
        product_category: store.productCategory,
        target_audience: store.targetAudience,
      });
      store.setReport(data.report, data.report_id, data.source_meta || null);
      loadHistory();
    } catch (err) {
      if (!handleApiError(err)) {
        const detail = err.response?.data?.detail;
        const errorMsg = typeof detail === 'string' ? detail : (detail?.message || 'Analysis failed. Please try again.');
        store.setError(errorMsg);
      } else {
        store.setLoading(false, 0);
      }
    }
  }, [store.query, store.targetMarket, store.productCategory, store.targetAudience]);

  const loadReport = async (reportId) => {
    store.setLoading(true, 5);
    try {
      const { data } = await axios.get(`${API}/api/competitive-intel/report/${reportId}`);
      store.setReport(data.report, data.report_id, data.source_meta || null);
      store.setField('query', data.query || '');
      store.setField('drawerOpen', false);
    } catch {
      store.setError('Failed to load report.');
    }
  };

  const deleteReport = async (reportId) => {
    try {
      await axios.delete(`${API}/api/competitive-intel/report/${reportId}`);
      store.setSavedReports(store.savedReports.filter(r => r.report_id !== reportId));
    } catch {}
  };

  const runCompare = async (reportId, competitor) => {
    store.setField('compareLoading', true);
    try {
      const { data } = await axios.post(`${API}/api/competitive-intel/compare`, {
        report_id: reportId,
        competitor_name: competitor.name,
        competitor_data: competitor,
      });
      store.setField('compareResult', data);
      store.setField('compareLoading', false);
    } catch (err) {
      if (!handleApiError(err)) {
        store.setField('compareLoading', false);
      }
    }
  };

  const openCompare = (comp) => {
    store.setField('compareModal', comp);
    store.setField('compareResult', null);
  };

  const r = store.report;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="competitive-intel-page">
        <EarlyAccessBanner />

        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
            Competitive Intelligence Agent
          </h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
            Find your real competitors, analyze their positioning, and discover how your brand can stand out.
          </p>
        </div>

        {/* Input Card */}
        <div className="rounded-xl p-5 lg:p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex gap-3">
            <div className="flex-1">
              <input data-testid="intel-query-input" value={store.query} onChange={e => store.setField('query', e.target.value)}
                placeholder="Paste your website, Instagram handle, or brand name..."
                className="w-full py-3 px-4 rounded-xl text-sm"
                onKeyDown={e => e.key === 'Enter' && !store.loading && analyze()}
                style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
            </div>
            <button onClick={analyze} disabled={!store.query.trim() || store.loading} data-testid="intel-analyze-btn"
              className="px-5 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 shrink-0 transition-all"
              style={{ background: store.loading ? 'rgba(200,240,96,0.05)' : '#c8f060', color: store.loading ? '#c8f060' : '#080808', opacity: (!store.query.trim() || store.loading) ? 0.5 : 1 }}>
              {store.loading ? <ArrowsClockwise size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
              {store.loading ? 'Analyzing...' : 'Analyze Market (5 credits)'}
            </button>
          </div>

          {/* Optional fields */}
          <button onClick={() => setOptionsOpen(!optionsOpen)}
            className="flex items-center gap-1.5 mt-3 text-[11px] font-medium" style={{ color: 'rgba(245,240,232,0.25)' }}>
            {optionsOpen ? <CaretDown size={10} /> : <CaretRight size={10} />} Advanced options
          </button>
          {optionsOpen && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              {[
                { key: 'targetMarket', placeholder: 'Target market (e.g., US women 25-35)' },
                { key: 'productCategory', placeholder: 'Product category (e.g., skincare)' },
                { key: 'targetAudience', placeholder: 'Target audience (e.g., eco-conscious millennials)' },
              ].map(f => (
                <input key={f.key} value={store[f.key]} onChange={e => store.setField(f.key, e.target.value)}
                  placeholder={f.placeholder} className="py-2 px-3 rounded-lg text-xs"
                  style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.06)', color: '#f5f0e8' }} />
              ))}
            </div>
          )}

          {/* Saved reports link */}
          {store.savedReports.length > 0 && (
            <button onClick={() => store.setField('drawerOpen', true)} data-testid="open-saved-reports"
              className="flex items-center gap-1.5 mt-3 text-[11px] font-medium hover:underline" style={{ color: 'rgba(200,240,96,0.5)' }}>
              <Clock size={10} /> {store.savedReports.length} saved report{store.savedReports.length !== 1 ? 's' : ''}
            </button>
          )}
        </div>

        {/* Error */}
        {store.error && (
          <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: 'rgba(255,68,68,0.05)', border: '1px solid rgba(255,68,68,0.1)' }}>
            <Warning size={16} style={{ color: '#ff4444' }} />
            <p className="text-sm flex-1" style={{ color: '#ff4444' }}>{typeof store.error === 'string' ? store.error : (store.error?.message || 'An error occurred')}</p>
            <button onClick={analyze} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#f5f0e8' }}>Retry</button>
          </div>
        )}

        {/* Loading */}
        {store.loading && (
          <div className="rounded-xl p-8 lg:p-12" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-md mx-auto">
              {LOADING_STEPS.map((step, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  {i < store.loadingStep ? (
                    <CheckCircle size={16} weight="fill" style={{ color: '#c8f060' }} />
                  ) : i === store.loadingStep ? (
                    <ArrowsClockwise size={16} className="animate-spin" style={{ color: '#c8f060' }} />
                  ) : (
                    <div className="w-4 h-4 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  )}
                  <span className="text-sm" style={{ color: i <= store.loadingStep ? 'rgba(245,240,232,0.7)' : 'rgba(245,240,232,0.15)' }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {r && !store.loading && (
          <div className="space-y-4">
            {/* Source Transparency */}
            <SourceBadge sourceMeta={store.sourceMeta} />

            {/* Action bar */}
            <div className="flex flex-wrap gap-2">
              <button onClick={analyze} data-testid="refresh-analysis" className="px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: '#c8f060' }}>
                <ArrowsClockwise size={12} /> Refresh Analysis
              </button>
              <button onClick={() => store.reset()} className="px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.4)' }}>
                New Analysis
              </button>
            </div>

            <BrandOverview data={r.overview} />
            <CompetitorTabs competitors={r.competitors} onCompare={openCompare} />

            {/* Positioning */}
            {r.positioningData?.length > 0 && (
              <Sec title="Positioning Breakdown" icon={ChartBar} color="#a78bfa">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {r.positioningData.map((p, i) => (
                    <div key={i} className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      <p className="text-sm font-semibold mb-2" style={{ color: '#f5f0e8' }}>{p.competitorName}</p>
                      {[['Sells', p.sells], ['Aesthetic', p.aesthetic], ['Messaging', p.messagingStyle]].map(([l, v]) => v && (
                        <div key={l} className="mb-1.5"><p className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(245,240,232,0.25)' }}>{l}</p><p className="text-xs" style={{ color: 'rgba(245,240,232,0.6)' }}>{v}</p></div>
                      ))}
                    </div>
                  ))}
                </div>
              </Sec>
            )}

            {/* Content Intelligence */}
            {r.contentInsights && (
              <Sec title="What's working in your market" icon={TrendUp} color="#22d3ee">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Dominant Formats', items: r.contentInsights.dominantFormats },
                    { label: 'Common Hooks', items: r.contentInsights.commonHooks },
                    { label: 'Visual Patterns', items: r.contentInsights.visualPatterns },
                    { label: 'Standout Insights', items: r.contentInsights.standoutInsights },
                  ].map(group => group.items?.length > 0 && (
                    <div key={group.label}>
                      <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'rgba(245,240,232,0.25)' }}>{group.label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.items.map((item, i) => <Tag key={i}>{item}</Tag>)}
                      </div>
                    </div>
                  ))}
                </div>
              </Sec>
            )}

            {/* Opportunity Gaps */}
            {r.gaps && (
              <Sec title="Where your brand can stand out" icon={Lightbulb} color="#f59e0b">
                {r.gaps.summary && <p className="text-sm mb-4 font-medium" style={{ color: 'rgba(245,158,11,0.7)' }}>{r.gaps.summary}</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Missed Angles', items: r.gaps.missedAngles, color: '#f59e0b' },
                    { label: 'Underserved Audiences', items: r.gaps.underservedAudiences, color: '#a78bfa' },
                    { label: 'Whitespace Opportunities', items: r.gaps.whitespaceOpportunities, color: '#c8f060' },
                    { label: 'Market Weaknesses', items: r.gaps.marketWeaknesses, color: '#ff4444' },
                  ].map(group => group.items?.length > 0 && (
                    <div key={group.label}>
                      <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: `${group.color}80` }}>{group.label}</p>
                      {group.items.map((item, i) => <InsightItem key={i} text={item} />)}
                    </div>
                  ))}
                </div>
              </Sec>
            )}

            {/* Strategy */}
            {r.strategy && (
              <Sec title="Your Competitive Strategy" icon={Strategy} color="#ec4899">
                <div className="space-y-4">
                  {[
                    { label: 'Positioning', value: r.strategy.positioning },
                    { label: 'Content Direction', value: r.strategy.contentDirection },
                    { label: 'Offer Strategy', value: r.strategy.offerStrategy },
                    { label: 'Messaging Angle', value: r.strategy.messagingAngle },
                  ].map(s => s.value && (
                    <div key={s.label} className="p-3 rounded-lg" style={{ background: 'rgba(236,72,153,0.03)', border: '1px solid rgba(236,72,153,0.06)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'rgba(236,72,153,0.5)' }}>{s.label}</p>
                      <p className="text-sm" style={{ color: '#f5f0e8' }}>{s.value}</p>
                    </div>
                  ))}
                  {r.strategy.nextActions?.length > 0 && (
                    <div className="p-4 rounded-lg" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.08)' }}>
                      <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#c8f060' }}>Next 3 Actions</p>
                      {r.strategy.nextActions.map((a, i) => (
                        <div key={i} className="flex items-start gap-2.5 py-1.5">
                          <span className="text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060' }}>{i + 1}</span>
                          <span className="text-sm" style={{ color: 'rgba(245,240,232,0.7)' }}>{a}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Sec>
            )}
          </div>
        )}
      </div>

      {/* Compare Modal */}
      <CompareModal
        competitor={store.compareModal}
        result={store.compareResult}
        loading={store.compareLoading}
        reportId={store.reportId}
        onClose={() => { store.setField('compareModal', null); store.setField('compareResult', null); }}
        onRun={runCompare}
      />

      {/* Saved Reports Drawer */}
      <AnimatePresence>
        {store.drawerOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex justify-end" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => store.setField('drawerOpen', false)}>
            <motion.div initial={{ x: 340 }} animate={{ x: 0 }} exit={{ x: 340 }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              className="w-96 h-full overflow-y-auto" style={{ background: '#111', borderLeft: '1px solid rgba(255,255,255,0.08)' }}
              onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between" style={{ background: '#111', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>Saved Reports</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'rgba(245,240,232,0.25)' }}>{store.savedReports.length} report{store.savedReports.length !== 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => store.setField('drawerOpen', false)} className="p-2 rounded-lg hover:bg-white/5 transition-colors">
                  <X size={16} style={{ color: 'rgba(245,240,232,0.4)' }} />
                </button>
              </div>
              <div className="p-4 space-y-2">
                {store.savedReports.length === 0 ? (
                  <div className="text-center py-12">
                    <FloppyDisk size={28} className="mx-auto mb-3" style={{ color: 'rgba(245,240,232,0.1)' }} />
                    <p className="text-xs" style={{ color: 'rgba(245,240,232,0.25)' }}>No saved reports yet</p>
                  </div>
                ) : store.savedReports.map(r => (
                  <motion.div key={r.report_id} whileHover={{ scale: 1.01 }}
                    className="p-4 rounded-xl cursor-pointer transition-colors hover:bg-white/[0.03]" 
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                    onClick={() => loadReport(r.report_id)}>
                    <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>{r.query}</p>
                    {r.report?.overview?.niche && (
                      <p className="text-[11px] mt-1 truncate" style={{ color: 'rgba(200,240,96,0.5)' }}>{r.report.overview.niche}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.2)' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        {r.credits_used && ` \u00b7 ${r.credits_used} credits`}
                      </p>
                      <button onClick={(e) => { e.stopPropagation(); deleteReport(r.report_id); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 transition-colors" style={{ color: 'rgba(255,68,68,0.5)' }}>
                        <Trash size={12} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
